import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Zap, Sparkles, Rocket, Brain, Eye, Target, Send } from 'lucide-react';

const PerennialLabs = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [logoAnimated, setLogoAnimated] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (currentPage !== 'home') {
      setLogoAnimated(true);
    } else {
      setLogoAnimated(false);
    }
  }, [currentPage]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    const handleScroll = () => {
      
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    for (let i = 0; i < 50; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(139, 92, 246, 0.1)';
      
      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();
        
        particles.forEach((p2, j) => {
          if (i !== j) {
            const dx = p.x - p2.x;
            const dy = p.y - p2.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 100) {
              ctx.strokeStyle = `rgba(139, 92, 246, ${0.1 * (1 - dist / 100)})`;
              ctx.lineWidth = 0.5;
              ctx.beginPath();
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(p2.x, p2.y);
              ctx.stroke();
            }
          }
        });
      });
      requestAnimationFrame(animate);
    };
    animate();
  }, []);

  const navigation = [
    { name: 'Reality', id: 'home', icon: <Eye className="w-4 h-4" /> },
    { name: 'Capabilities', id: 'services', icon: <Brain className="w-4 h-4" /> },
    { name: 'Vision', id: 'about', icon: <Target className="w-4 h-4" /> },
    { name: 'Creations', id: 'portfolio', icon: <Sparkles className="w-4 h-4" /> },
    { name: 'Connect', id: 'contact', icon: <Send className="w-4 h-4" /> }
  ];

  return (
    <div className="relative bg-black text-white overflow-hidden">
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes glitch {
          0% { transform: translate(0); }
          20% { transform: translate(-2px, 2px); }
          40% { transform: translate(-2px, -2px); }
          60% { transform: translate(2px, 2px); }
          80% { transform: translate(2px, -2px); }
          100% { transform: translate(0); }
        }
        @keyframes rotate3d {
          0% { transform: rotateY(0deg) rotateX(0deg); }
          100% { transform: rotateY(360deg) rotateX(360deg); }
        }
        @keyframes pulse-border {
          0%, 100% { border-color: rgba(168, 85, 247, 0.5); }
          50% { border-color: rgba(236, 72, 153, 0.8); }
        }
        @keyframes text-shimmer {
          0% { background-position: -100% 0; }
          100% { background-position: 200% 0; }
        }
        .text-shimmer {
          background: linear-gradient(90deg, #fff 0%, #a78bfa 25%, #ec4899 50%, #60a5fa 75%, #fff 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: text-shimmer 3s linear infinite;
        }
      `}</style>

      {/* Animated Logo in Top Left - Shows when not on home */}
      {logoAnimated && (
        <div className="fixed top-8 left-8 z-50 cursor-pointer group" onClick={() => setCurrentPage('home')}>
          <div className="animate-fade-in relative">
            {/* Glowing background effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-purple-600/20 to-pink-600/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg"></div>
            
            <div className="relative">
              <div className="text-2xl font-black leading-none flex gap-0.5 mb-1">
                {['P','E','R','E','N','N','I','A','L'].map((letter, i) => {
                  const isGradient = i === 1 || i === 3 || i === 6;
                  const gradientColors = i === 1 ? 'from-purple-400 to-pink-400' : 
                                        i === 3 ? 'from-pink-400 to-blue-400' : 
                                        'from-blue-400 to-purple-400';
                  return (
                    <span 
                      key={i}
                      className={`hover:scale-125 transition-all duration-300 cursor-pointer ${
                        isGradient ? `bg-gradient-to-r ${gradientColors} bg-clip-text text-transparent` : ''
                      }`}
                      style={{
                        animation: `slideIn 0.3s ease-out ${i * 0.05}s both`,
                        display: 'inline-block'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.animation = 'glitch 0.3s ease';
                        setTimeout(() => {
                          e.target.style.animation = '';
                        }, 300);
                      }}
                    >
                      {letter}
                    </span>
                  );
                })}
              </div>
              <div className="text-sm tracking-widest bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent group-hover:tracking-wide transition-all duration-300">
                LABS
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animated Canvas Background */}
      <canvas ref={canvasRef} className="fixed inset-0 z-0 pointer-events-none" />

      {/* Custom Cursor */}
      <div 
        className="fixed w-6 h-6 border-2 border-purple-500 rounded-full pointer-events-none z-50 mix-blend-difference transition-transform duration-100"
        style={{ 
          left: `${mousePos.x}px`, 
          top: `${mousePos.y}px`,
          transform: 'translate(-50%, -50%)'
        }}
      />

      {/* Radial gradient following mouse */}
      <div 
        className="fixed w-96 h-96 pointer-events-none z-0 opacity-20 blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(139,92,246,0.4) 0%, transparent 70%)',
          left: `${mousePos.x}px`,
          top: `${mousePos.y}px`,
          transform: 'translate(-50%, -50%)'
        }}
      />

      {/* Floating Navigation */}
      <nav className="fixed top-8 left-1/2 -translate-x-1/2 z-40">
        <div className="hidden md:flex items-center gap-2 bg-black/40 backdrop-blur-2xl border border-white/10 rounded-full px-6 py-3 shadow-2xl">
          {navigation.map((item) => (
            <button
              key={item.id}
              onClick={() => setCurrentPage(item.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${
                currentPage === item.id 
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' 
                  : 'hover:bg-white/5 text-gray-400 hover:text-white'
              }`}
            >
              {item.icon}
              <span className="text-sm font-medium">{item.name}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Mobile Menu */}
      <button 
        onClick={() => setMenuOpen(!menuOpen)}
        className="md:hidden fixed top-8 right-8 z-50 w-12 h-12 bg-black/40 backdrop-blur-2xl border border-white/10 rounded-full flex items-center justify-center"
      >
        {menuOpen ? <X /> : <Menu />}
      </button>

      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/95 backdrop-blur-xl flex items-center justify-center">
          <div className="space-y-6">
            {navigation.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setCurrentPage(item.id);
                  setMenuOpen(false);
                }}
                className="flex items-center gap-4 text-3xl hover:text-purple-400 transition-colors"
              >
                {item.icon}
                {item.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="relative z-10">
        {/* HOME - Experimental Hero */}
        {currentPage === 'home' && (
          <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-pink-900/20"></div>
            
            {/* Floating geometric shapes */}
            <div className="absolute top-20 left-10 w-32 h-32 border border-purple-500/30 rotate-45 animate-spin" style={{ animationDuration: '20s' }}></div>
            <div className="absolute bottom-20 right-20 w-48 h-48 border border-pink-500/30 rounded-full animate-pulse"></div>
            <div className="absolute top-1/2 right-10 w-24 h-24 bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-3xl rotate-12 animate-bounce" style={{ animationDuration: '3s' }}></div>

            <div className="max-w-6xl text-center relative">
              <div className="mb-8 inline-block relative">
              
                
                <div className="text-8xl md:text-9xl font-black leading-none mb-4 relative">
                  <span className="inline-block hover:scale-110 transition-transform cursor-default hover:rotate-12">P</span>
                  <span className="inline-block hover:scale-110 transition-transform cursor-default bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent hover:-rotate-6">E</span>
                  <span className="inline-block hover:scale-110 transition-transform cursor-default hover:rotate-6">R</span>
                  <span className="inline-block hover:scale-110 transition-transform cursor-default bg-gradient-to-r from-pink-400 to-blue-400 bg-clip-text text-transparent hover:-rotate-12">E</span>
                  <span className="inline-block hover:scale-110 transition-transform cursor-default hover:rotate-3">N</span>
                  <span className="inline-block hover:scale-110 transition-transform cursor-default hover:-rotate-3">N</span>
                  <span className="inline-block hover:scale-110 transition-transform cursor-default bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent hover:rotate-12">I</span>
                  <span className="inline-block hover:scale-110 transition-transform cursor-default hover:-rotate-6">A</span>
                  <span className="inline-block hover:scale-110 transition-transform cursor-default hover:rotate-6">L</span>
                </div>
                <div className="text-6xl md:text-7xl font-black tracking-widest relative">
                  <span className="text-shimmer">
                    LABS
                  </span>
                </div>
              </div>

              <p className="text-2xl md:text-3xl mb-12 font-light tracking-wide text-gray-300 max-w-3xl mx-auto">
                We don't build websites. We <span className="italic text-purple-400">craft digital realities</span> that defy convention.
              </p>

              <div className="flex flex-wrap gap-4 justify-center mb-16">
                <button 
                  onClick={() => setCurrentPage('contact')}
                  className="group relative px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-none overflow-hidden hover:scale-105 transition-transform"
                >
                  <span className="relative z-10 flex items-center gap-2 font-bold">
                    INITIATE PROJECT
                    <Rocket className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </button>
                
                <button 
                  onClick={() => setCurrentPage('portfolio')}
                  className="px-8 py-4 border-2 border-white/20 rounded-none hover:bg-white/5 transition-all backdrop-blur-sm font-bold"
                >
                  WITNESS INNOVATION
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {[
                  { num: '∞', label: 'POSSIBILITIES' },
                  { num: '150+', label: 'REALITIES BUILT' },
                  { num: '0.1s', label: 'TO AMAZEMENT' },
                  { num: '100%', label: 'FUTURE-PROOF' }
                ].map((stat, i) => (
                  <div key={i} className="group cursor-default">
                    <div className="text-5xl font-black text-purple-400 mb-2 group-hover:scale-125 transition-transform">
                      {stat.num}
                    </div>
                    <div className="text-xs tracking-widest text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* SERVICES - Unconventional Grid */}
        {currentPage === 'services' && (
          <div className="min-h-screen py-32 px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-7xl font-black mb-20 text-center">
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                  CAPABILITIES
                </span>
              </h2>

              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { icon: <Brain className="w-12 h-12" />, title: 'NEURAL INTERFACES', desc: 'AI-powered experiences that think', color: 'from-purple-600/20 to-purple-900/20', border: 'border-purple-500/50' },
                  { icon: <Zap className="w-12 h-12" />, title: 'QUANTUM SPEED', desc: 'Microsecond load times, instant interactions', color: 'from-yellow-600/20 to-orange-900/20', border: 'border-yellow-500/50' },
                  { icon: <Sparkles className="w-12 h-12" />, title: 'REALITY BENDING', desc: 'Immersive 3D & WebGL experiences', color: 'from-pink-600/20 to-pink-900/20', border: 'border-pink-500/50' },
                  { icon: <Target className="w-12 h-12" />, title: 'PRECISION CRAFT', desc: 'Pixel-perfect, obsessively detailed', color: 'from-blue-600/20 to-blue-900/20', border: 'border-blue-500/50' },
                  { icon: <Eye className="w-12 h-12" />, title: 'VISUAL SORCERY', desc: 'Motion design that hypnotizes', color: 'from-green-600/20 to-green-900/20', border: 'border-green-500/50' },
                  { icon: <Rocket className="w-12 h-12" />, title: 'ZERO LIMITS', desc: 'Pushing boundaries beyond imagination', color: 'from-red-600/20 to-red-900/20', border: 'border-red-500/50' },
                ].map((service, i) => (
                  <div
                    key={i}
                    className={`group relative p-8 bg-gradient-to-br ${service.color} border ${service.border} backdrop-blur-xl hover:scale-105 transition-all duration-500 cursor-pointer overflow-hidden`}
                    style={{ clipPath: i % 2 === 0 ? 'polygon(0 0, 100% 0, 100% 85%, 0 100%)' : 'polygon(0 0, 100% 0, 100% 100%, 0 85%)' }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative z-10">
                      <div className="mb-6 text-white/80 group-hover:text-white transition-colors group-hover:scale-110 inline-block transition-transform">
                        {service.icon}
                      </div>
                      <h3 className="text-2xl font-black mb-3 tracking-tight">{service.title}</h3>
                      <p className="text-gray-400 text-sm">{service.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-20 text-center">
                <p className="text-2xl text-gray-400 mb-8">Need something that doesn't exist yet?</p>
                <button 
                  onClick={() => setCurrentPage('contact')}
                  className="px-12 py-5 bg-white text-black font-black text-lg hover:bg-purple-400 hover:text-white transition-all"
                >
                  LET'S INVENT IT
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ABOUT - Split Screen */}
        {currentPage === 'about' && (
          <div className="min-h-screen flex items-center px-6 py-32">
            <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-7xl font-black mb-12 leading-tight">
                  WE ARE<br/>
                  <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                    NOT NORMAL
                  </span>
                </h2>
                <div className="space-y-6 text-xl text-gray-300 leading-relaxed">
                  <p>
                    PerennialLabs exists in the intersection of <span className="text-purple-400 font-bold">madness</span> and <span className="text-pink-400 font-bold">genius</span>.
                  </p>
                  <p>
                    We rejected the template. Burned the rulebook. Rewrote reality.
                  </p>
                  <p>
                    Every pixel is a rebellion. Every animation, an act of defiance against mediocrity.
                  </p>
                  <p className="text-2xl font-bold text-white">
                    We don't follow trends.<br/>
                    <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                      We CREATE them.
                    </span>
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="aspect-square bg-gradient-to-br from-purple-600/20 to-pink-600/20 border-4 border-white/10 backdrop-blur-xl relative overflow-hidden group">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-600/0 to-pink-600/40 opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
                  {[...Array(20)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-1 h-full bg-white/5"
                      style={{ left: `${i * 5}%` }}
                    ></div>
                  ))}
                  {[...Array(20)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-full h-1 bg-white/5"
                      style={{ top: `${i * 5}%` }}
                    ></div>
                  ))}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center p-8">
                      <div className="text-9xl font-black mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                        ∞
                      </div>
                      <div className="text-3xl font-black tracking-widest">INFINITE</div>
                      <div className="text-xl text-gray-400">INNOVATION</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PORTFOLIO - Masonry Chaos */}
        {currentPage === 'portfolio' && (
          <div className="min-h-screen py-32 px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-7xl font-black mb-20 text-center">
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                  CREATIONS
                </span>
              </h2>

              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { title: 'NEXUS', desc: 'BLOCKCHAIN REVOLUTION', h: 'md:row-span-2', bg: 'from-purple-600 to-purple-900' },
                  { title: 'VOID', desc: 'DARK MODE PERFECTED', h: '', bg: 'from-gray-800 to-black' },
                  { title: 'QUANTUM', desc: 'TIME MANIPULATION UI', h: '', bg: 'from-blue-600 to-cyan-600' },
                  { title: 'SYNAPSE', desc: 'NEURAL NETWORK DASHBOARD', h: '', bg: 'from-pink-600 to-red-600' },
                  { title: 'AURORA', desc: 'GENERATIVE ART PLATFORM', h: 'md:row-span-2', bg: 'from-green-600 to-emerald-600' },
                  { title: 'ECLIPSE', desc: 'IMMERSIVE 3D EXPERIENCE', h: '', bg: 'from-orange-600 to-yellow-600' }
                ].map((project, i) => (
                  <div
                    key={i}
                    className={`group relative ${project.h} min-h-[300px] overflow-hidden cursor-pointer`}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${project.bg} transition-transform duration-700 group-hover:scale-110`}></div>
                    <div className="absolute inset-0 bg-black/60 group-hover:bg-black/20 transition-all duration-500"></div>
                    <div className="absolute inset-0 p-8 flex flex-col justify-end opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="text-xs tracking-widest text-white/60 mb-2">{project.desc}</div>
                      <div className="text-4xl font-black">{project.title}</div>
                    </div>
                    <div className="absolute top-8 right-8 text-8xl font-black text-white/5 group-hover:text-white/10 transition-colors">
                      {i + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CONTACT - Brutal Minimalism */}
        {currentPage === 'contact' && (
          <div className="min-h-screen flex items-center justify-center px-6 py-32">
            <div className="max-w-4xl w-full">
              <h2 className="text-7xl font-black mb-20 text-center">
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  CONNECT
                </span>
              </h2>

              <div className="space-y-6">
                <input
                  type="text"
                  placeholder="YOUR NAME"
                  className="w-full bg-transparent border-b-2 border-white/20 focus:border-purple-500 outline-none py-4 text-2xl transition-colors"
                />
                <input
                  type="email"
                  placeholder="YOUR EMAIL"
                  className="w-full bg-transparent border-b-2 border-white/20 focus:border-purple-500 outline-none py-4 text-2xl transition-colors"
                />
                <textarea
                  placeholder="YOUR VISION"
                  rows="6"
                  className="w-full bg-transparent border-b-2 border-white/20 focus:border-purple-500 outline-none py-4 text-2xl resize-none transition-colors"
                ></textarea>
                
                <button className="w-full py-8 bg-gradient-to-r from-purple-600 to-pink-600 text-3xl font-black hover:from-pink-600 hover:to-blue-600 transition-all duration-500 mt-8">
                  LAUNCH REALITY →
                </button>
              </div>

              <div className="mt-20 grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-purple-400 mb-2 text-sm tracking-widest">EMAIL</div>
                  <div className="font-bold">riteshkarki505@gmail.com</div>
                </div>
                <div>
                  <div className="text-purple-400 mb-2 text-sm tracking-widest">LOCATION</div>
                  <div className="font-bold">Nowhere</div>
                </div>
                <div>
                  <div className="text-purple-400 mb-2 text-sm tracking-widest">AVAILABILITY</div>
                  <div className="font-bold">24/7/365/∞</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer - Minimal */}
        <footer className="border-t border-white/10 py-12 px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-3xl font-black">
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                PERENNIAL
              </span>
              LABS
            </div>
            <div className="text-gray-500 text-sm tracking-widest">
              © 2026 — REALITY REDEFINED
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PerennialLabs;
